import { handleClientScriptLoad } from 'next/script';

import type {
  LoadScriptWithRetryOptions,
  LoadScriptWithRetryResult,
  ScriptAttemptResult,
} from './load-script-with-retry.types';

/** Default number of attempts for {@link loadScriptWithRetry}. */
export const DEFAULT_MAX_ATTEMPTS = 3;
/** Default backoff before each retry, in ms. */
export const DEFAULT_BACKOFF_MS = [1_000, 2_000, 4_000];
/** Default per-attempt timeout, in ms. */
export const DEFAULT_TIMEOUT_MS = 10_000;
/** Default time to wait for `isReady` after `load`, in ms. */
export const DEFAULT_READY_GRACE_MS = 1_000;

const READY_POLL_MS = 100;

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener('abort', done, { once: true });
  });
}

function waitForOnline(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (navigator.onLine !== false || signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      window.removeEventListener('online', done);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    window.addEventListener('online', done, { once: true });
    signal?.addEventListener('abort', done, { once: true });
  });
}

async function waitForReady(
  isReady: () => boolean,
  graceMs: number,
  signal?: AbortSignal,
): Promise<boolean> {
  for (let elapsed = 0; elapsed < graceMs; elapsed += READY_POLL_MS) {
    if (isReady()) return true;
    // eslint-disable-next-line no-await-in-loop
    await wait(READY_POLL_MS, signal);
    if (signal?.aborted) return false;
  }
  return isReady();
}

function findScripts(src: string): HTMLScriptElement[] {
  return Array.from(document.querySelectorAll('script')).filter(
    (element) => element.getAttribute('src') === src,
  );
}

/**
 * Attempt 1 keeps `id` so a later `<Script id>` dedupes. Retries need a fresh
 * id: `next/script` caches by `id || src` and adds that key on `load`, then
 * returns without calling `onLoad` or injecting anything.
 */
function scriptIdForAttempt(id: string, attempt: number): string {
  return attempt === 1 ? id : `${id}-retry-${attempt}`;
}

function attemptLoad(
  id: string,
  src: string,
  timeoutMs: number,
): Promise<'success' | 'error' | 'timeout'> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (result: 'success' | 'error' | 'timeout') => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => settle('timeout'), timeoutMs);

    // Going through next/script (instead of a raw <script>) registers the id
    // in its load cache on success, so any later `<Script id={id}>` (e.g. on a
    // TYP page) dedupes instead of injecting the vendor script a second time.
    handleClientScriptLoad({
      id,
      src,
      onLoad: () => settle('success'),
      onError: () => settle('error'),
    });
  });
}

/**
 * Loads a third-party script, retrying with exponential backoff when it fails.
 *
 * Each attempt injects the script through `next/script`'s
 * `handleClientScriptLoad` with a distinct `src` (see
 * {@link LoadScriptWithRetryOptions.buildSrc}) and, after the first attempt, a
 * distinct id (see {@link scriptIdForAttempt}). A failed attempt's element is
 * removed before retrying; a timed-out one loses its `id`. While the browser
 * is offline, the next attempt waits for the `online` event. Never rejects.
 *
 * @returns Whether the script loaded and how many attempts were started.
 */
export async function loadScriptWithRetry({
  id,
  buildSrc,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  backoffMs = DEFAULT_BACKOFF_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  isReady,
  readyGraceMs = DEFAULT_READY_GRACE_MS,
  onAttempt,
  signal,
}: LoadScriptWithRetryOptions): Promise<LoadScriptWithRetryResult> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (attempt > 1) {
      const delay = backoffMs[Math.min(attempt - 2, backoffMs.length - 1)] ?? 0;
      // eslint-disable-next-line no-await-in-loop
      await wait(delay, signal);
      // A timed-out attempt can still finish late; don't inject a second copy
      // of a script that has meanwhile initialised.
      if (isReady?.()) return { loaded: true, attempts: attempt - 1 };
    }
    // eslint-disable-next-line no-await-in-loop
    await waitForOnline(signal);
    if (signal?.aborted) return { loaded: false, attempts: attempt - 1 };

    const src = buildSrc(attempt);
    const startedAt = Date.now();
    // eslint-disable-next-line no-await-in-loop
    let result: ScriptAttemptResult = await attemptLoad(
      scriptIdForAttempt(id, attempt),
      src,
      timeoutMs,
    );

    if (result === 'success' && isReady) {
      // eslint-disable-next-line no-await-in-loop
      const ready = await waitForReady(isReady, readyGraceMs, signal);
      if (!ready) result = 'not-initialised';
    }

    if (signal?.aborted) {
      return { loaded: result === 'success', attempts: attempt };
    }

    if (result === 'error' || result === 'not-initialised') {
      findScripts(src).forEach((element) => element.remove());
    } else if (result === 'timeout') {
      findScripts(src).forEach((element) => element.removeAttribute('id'));
    }

    onAttempt?.({ attempt, result, durationMs: Date.now() - startedAt });

    if (result === 'success') return { loaded: true, attempts: attempt };
  }

  return { loaded: false, attempts: maxAttempts };
}
